import {
  EntityIcon,
  WbDisclosure,
  WbBreadcrumb,
  WbBreadcrumbs,
  WbDivider,
  WbFavoriteEntityButton,
} from '@agilelab/plugin-wb-platform';
import { Box, createStyles, ListItem, makeStyles } from '@material-ui/core';
import { EntitySummary } from '../../Entity/EntitySummary';
import { TypeBadge } from '../../Entity/TypeBadge';
import { SearchFlagBox } from '../components/SearchFlagBox';
import { SearchSystemActionsMenu } from '../components/SearchSystemActionsMenu';
import { CustomViews } from '../CustomViews';
import { MarketplaceSearchResultEnriched, SearchResultInfo } from '../types';
/**
 * Props for {@link MarketplaceSearchListItem}.
 *
 * @public
 */
export interface MarketplaceSearchListItemProps {
  searchResult: MarketplaceSearchResultEnriched;
}

export const KIND_TO_INFO_COMPONENT: Record<string, SearchResultInfo> = {
  system: CustomViews.SearchResultSystemInfo,
  component: CustomViews.SearchResultComponentInfo,
};

const useStyles = makeStyles(theme =>
  createStyles({
    actions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
    },
  }),
);

/** @public */
export function MarketplaceSearchListItem(
  props: MarketplaceSearchListItemProps,
) {
  const { document: searchDocument } = props.searchResult;
  const documentKind = searchDocument._computedInfo.kind;

  const { location } = searchDocument;
  const classes = useStyles();

  const InfoComponent = KIND_TO_INFO_COMPONENT[documentKind];
  return (
    <ListItem style={{ paddingLeft: 0 }}>
      <WbDisclosure
        title={
          <EntitySummary
            location={location}
            leading={
              <EntityIcon
                title={searchDocument._nameWithVersion}
                useCaseTemplateId={searchDocument.useCaseTemplateId}
                kind={documentKind}
                taxonomyUrn={searchDocument._computedInfo.taxonomy.external_id}
                style={{ flexShrink: 0 }}
              />
            }
            title={searchDocument._nameWithVersion}
            breadcrumbs={
              <WbBreadcrumbs variant="secondary" disableLastItemHighlight>
                {searchDocument._resolvedDomains.map(d => (
                  <WbBreadcrumb key={d}>{d}</WbBreadcrumb>
                ))}
                {/* system */}
                {searchDocument._systemParentDisplayName && (
                  <WbBreadcrumb>
                    {searchDocument._systemParentDisplayName}
                  </WbBreadcrumb>
                )}
                {/* component */}
                {searchDocument._componentParentDisplayName && (
                  <WbBreadcrumb>
                    {searchDocument._componentParentDisplayName}
                  </WbBreadcrumb>
                )}
              </WbBreadcrumbs>
            }
          />
        }
        actions={
          <Box className={classes.actions}>
            <SearchFlagBox externalId={searchDocument.id} />
            <TypeBadge
              typeDisplayName={searchDocument._resolvedType}
              kind={documentKind}
            />

            <WbFavoriteEntityButton
              entity={`search:${props.searchResult.document.id}`}
            />
            {documentKind === 'system' && (
              <>
                <WbDivider />
                <SearchSystemActionsMenu
                  inDataContractLineage={
                    searchDocument._computedInfo.in_data_contract_lineage
                  }
                  dataProductId={searchDocument.documentId}
                />
              </>
            )}
          </Box>
        }
        renderDynamicContent={isExpanded => (
          <InfoComponent
            data={{
              ...searchDocument,
              _expanded: isExpanded || undefined,
            }}
          />
        )}
        placeholderHeight="144px" // This is the height of the collapsed accordion to avoid snapping on render
      />
    </ListItem>
  );
}
